/* global BaseUrl, EpgEnabled, SalesforceMobileLinksRedirect */
import { browserHistory } from 'react-router';
import _ from 'lodash';
import { getBaseContentUrl } from 'helpers/ApiHelper';
import { setOptOutCCPACookie } from 'helpers/ConfigHelper';
import { getSessionStorageItem, setSessionStorageItem, setCookie } from 'helpers/Storage';
import { getAppUrlList } from 'components/Header/HeaderHelpers';
import { CHANNEL_PRESELECT } from '../screens/Live/LiveHelpers';

const APPVIEW_EXCLUDE = ['Account', 'Header', 'Footer'];
export const LAST_SESSION_PATH = 'lastFoxPath';
const LAST_SETTING_PATH = 'lastSettingPath';
const COOKIE_SET_PATH_FOR_RE_ENTRY_REDIRECT = 'COOKIE_SET_PATH_FOR_RE_ENTRY_REDIRECT';

export const cleanString = string => _.toLower(string).replace(/[^a-z0-9\s()-]/g, '');

/**
 * Returns the base url slug, if it exists
 */
export const helperGetBaseUrlSlug = () => _.get(window.ExternalConfig, 'baseUrlSlug', '');

/**
 * Navigates to a direct page uri
 * @param {String} uri    - Code for the show page to load
 */
export const helperGetShowUrl = uri => `${helperGetBaseUrlSlug()}/${uri}/`;

export const helperGetPersonalityUrl = uri => `${helperGetBaseUrlSlug()}/personalities/${uri}/`;

export const helperGetShowClipsUrl = uri => `${helperGetShowUrl(uri)}clips/`;

export const helperGetFNCLiveUrl = () => '/live-news/';
/**
 * Navigates to a watch page
 * @param {String} uri    - Video code that represents the video page to load
 */
export const helperGetVideoUrl = (uri, hash = null) => {
  const hasQueryString = uri && _.includes(uri, '?');
  const trailingSlash = hasQueryString ? '' : '/';
  const watchUrl = `${helperGetBaseUrlSlug()}/watch/${uri}${trailingSlash}`;
  if (hash) {
    return `${watchUrl}${hash}`;
  }
  return watchUrl;
};

/**
 * Navigates to the live page
 */
export const helperGetLiveUrl = ({ callSign, videoId = '' } = {}) => {
  let slug = '';
  if (callSign && EpgEnabled) {
    const id = videoId ? `?id=${videoId}` : '';
    slug = `${CHANNEL_PRESELECT}/${callSign}/${id}`;
  } else if (videoId && !EpgEnabled) {
    slug = `${videoId}/`;
  }
  return `${helperGetBaseUrlSlug()}/live/${slug}`;
};

export const helperGetLiveScheduleUrl = callSign => {
  const liveUri = helperGetLiveUrl({ callSign });
  return `${liveUri}schedule/`;
};

/**
 * Link to static content
 */
export const helperGetStaticContentUrl = (contentType, contentName) => {
  let url = `${helperGetBaseUrlSlug()}/static/`;
  if (contentType) url = `${url}${contentType}/`;
  if (contentName) url = `${url}${contentName}`;
  return url;
};

/**
 * Navigates to the home page
 */
export const helperGetHomeUrl = () => `${helperGetBaseUrlSlug()}/`;

/**
 * Navigates to the shows landing page
 */
export const helperGetShowsUrl = () => `${helperGetBaseUrlSlug()}/shows/`;
export const helperGetNewsUrl = () => `${helperGetBaseUrlSlug()}/news/`;
export const helperGetSportsUrl = () => `${helperGetBaseUrlSlug()}/sports/`;

/**
 * Navigates to the personalities landing page
 */
export const helperGetPersonalitiesUrl = () => `${helperGetBaseUrlSlug()}/personalities/`;

/**
 * Navigates to a movie page
 * @param {String} uri    - Video code that represents the movie page to load
 */
const helperGetMovieUrl = uri => `${helperGetBaseUrlSlug()}/${uri}/`;

/**
 * Navigates to the account page
 */
export const helperGetAccountUrl = () => `${helperGetBaseUrlSlug()}/account/`;

/**
 * Navigates to the networkUpsell page
 */
export const helperGetNetworkUpsellUrl = () => `${helperGetBaseUrlSlug()}/`;

/**
 * Navigates to the find page
 */
export const helperGetFindUrl = (originalCollection, filter, campaignCodes) => {
  let collectionElement = '';
  if (originalCollection) {
    const decodedCollection = decodeURIComponent(originalCollection);
    const collection = encodeURIComponent(cleanString(decodedCollection));
    collectionElement = `${collection}/`;
  }
  const queryParams = campaignCodes ? `${campaignCodes}` : '';
  const filterElement = filter ? `filter/${filter}/` : '';
  return `${helperGetBaseUrlSlug()}/find/${collectionElement}${filterElement}${queryParams}`;
};

/**
 * Navigates to an article page
 * @param {String} uri    - Article code that represents the article page to load
 */
export const helperGetArticleUrl = uri => `${helperGetBaseUrlSlug()}/article/${uri}/`;

export const helperGetSeriesArticleUrl = uri => `${helperGetBaseUrlSlug()}/${uri}/`;

/**
 * Navigates to a photo gallery page
 * @param {String} uri    - Photo gallery code that represents the Photo gallery page to load
 */
const helperGetGalleryUrl = uri => `${helperGetBaseUrlSlug()}/photo-gallery/${uri}/`;

/**
 * Navigates to a show's article page
 * @param {String} showUri    - Show code that represents the show to use
 * @param {String} articleUri    - Article code that represents the article page to load
 */
const helperGetShowArticleUrl = (showUri, articleUri) =>
  `${helperGetShowUrl(showUri)}article/${articleUri}/`;

/**
 * Navigates to a show's photo gallery page
 * @param {String} showUri    - Show code that represents the show to use
 * @param {String} galleryUri    - Photo gallery code that represents the gallery page to load
 */
const helperGetShowGalleryUrl = (showUri, galleryUri) =>
  `${helperGetShowUrl(showUri)}photo-gallery/${galleryUri}/`;

/**
 * Navigates to a show's page detail
 * @param {String} showUri    - Show code that represents the show to use
 * @param {String} pageUri    - Page code to load
 */
const helperGetShowPageUrl = (showUri, pageUri) => {
  const show = helperGetShowUrl(showUri);
  if (pageUri) return `${show}${pageUri}/`;
  return show;
};

/**
 * Navigates to a show bio landing page
 * @param {String} showUri  - Show Uri
 */
export const helperGetBioLandingUrl = showUri => `${helperGetShowUrl(showUri)}credits/`;
export const helperGetCharacterLandingUrl = showUri => `${helperGetShowUrl(showUri)}characters/`;

/**
 * Navigates to a show bio page of a cast member
 * @param {String} showUri  - Show Uri
 * @param {String} bioUri   - Bio Uri
 */
export const helperGetBioUrl = (showUri, bioUri) => `${helperGetBioLandingUrl(showUri)}${bioUri}/`;
export const helperGetCharUrl = (showUri, bioUri) =>
  `${helperGetCharacterLandingUrl(showUri)}${bioUri}/`;

const helperGetCollectionFilterUrl = (baseUri, collectionId, filterId) => {
  if (collectionId && filterId) {
    return `${baseUri}collection/${collectionId}/filter/${filterId}/`;
  }

  if (collectionId) {
    return `${baseUri}collection/${collectionId}/`;
  }

  if (filterId) {
    return `${baseUri}filter/${filterId}/`;
  }

  return baseUri;
};

export const helperGetShowsCollectionFilterUrl = (collectionId, filterId) => {
  const baseUri = helperGetShowsUrl();
  return helperGetCollectionFilterUrl(baseUri, collectionId, filterId);
};

export const helperGetSeriesDetailUrl = seriesUri => helperGetShowPageUrl(seriesUri, '');

/**
 * Navigates to a show page uri
 * @param {String} uri    - Code for the show page to load
 */
export const helperGoToShowDetail = uri => {
  browserHistory.push(helperGetShowUrl(uri));
};

/**
 * Navigates to a watch page
 * @param {String} uri    - Video code that represents the video page to load
 */
export const helperGoToVideoDetail = (uri, hash = null) => {
  browserHistory.push(helperGetVideoUrl(uri, hash));
};

/**
 * Navigates to bio landing page
 * @param {String} showUri    - the show id
 */
export const helperGoToBioLanding = showUri => {
  browserHistory.push(helperGetBioLandingUrl(showUri));
};

/**
 * Navigates to a uri
 * @param {String} uri - Uri to go to
 */
export const helperGoToRelativeUrl = uri => {
  browserHistory.push(uri);
};

const helperRouteMapper = route => {
  const map = {
    'video-detail': helperGetVideoUrl,
    'movie-detail': helperGetMovieUrl,
    'show-landing': helperGetShowUrl,
    'article-detail': helperGetArticleUrl,
    'gallery-detail': helperGetGalleryUrl,
    'clip-detail': helperGetVideoUrl,
    player: helperGetVideoUrl,
    vodplayer: helperGetVideoUrl,
    'series-detail': helperGetShowUrl,
    'series-collection-detail': helperGetShowUrl,
    'special-detail': helperGetShowUrl,
    categories: helperGetPersonalityUrl,
  };

  if (route) return map[route];
  return null;
};

const map = {
  'article-detail': helperGetShowArticleUrl,
  'gallery-detail': helperGetShowGalleryUrl,
};
const helperRouteShowMapper = route => {
  if (route) return map[route] || helperGetShowPageUrl;
  return null;
};

/**
 * Takes a fully-qualified URL as an argument and returns a relative URL
 * if the fully-qualified URL begins with window.ExternalConfig's baseScreenUrl
 */
export const helperGetRelativeUrl = ({ url = null, showCode = '', hash = '' }) => {
  if (!url) return null;
  const urlComponents = url.split('?');
  const uri = _.get(urlComponents, '0');
  let params = null;
  const screenTxt = '/screens/';
  const screenLocation = uri.indexOf(screenTxt);
  const isWatchUrl = url?.indexOf('/v2.0/watch') >= 0;
  const baseContentUrl = getBaseContentUrl();
  if (screenLocation > 0) {
    params = uri.substr(screenTxt.length + screenLocation).split('/');
  } else if (isWatchUrl) {
    const { videoId } = getUrlParams(url);
    params = ['vodplayer', videoId];
  } else if (baseContentUrl && uri.startsWith(baseContentUrl)) {
    params = uri
      .split(baseContentUrl)
      .pop()
      .split('/');
  }
  if (params) {
    const [key, id] = params;
    if (key && id) {
      let getRelativeUrl;
      let relativeUrl;
      if (showCode) {
        getRelativeUrl = helperRouteShowMapper(key);
        if (getRelativeUrl) relativeUrl = getRelativeUrl(showCode, id);
      } else {
        getRelativeUrl = helperRouteMapper(key);
        if (getRelativeUrl) relativeUrl = getRelativeUrl(id);
      }
      if (!relativeUrl) return null;
      if (hash) relativeUrl = `${relativeUrl}#${hash}`;
      return relativeUrl;
    }
  }
  return null;
};

/**
 * Checks to see if a trailing slash exist, if not, a trailing slash is added of the current url
 * @param {Function} replace - react router's helper function
 */
const helperForceTrailingSlash = replace => {
  const path = window.location.pathname;
  if (path.slice(-1) !== '/') {
    replace({
      ...window.location,
      pathname: `${path}/`,
    });
  }
};

/**
 * Checks if any of the matching routes  has appView property set to true
 * @param {Array} - all Matching routes
 * @return {Boolean} - returns true or false based on whether any of the matching Routes is true
 */
export const isAppView = routes => _.some(routes, { appView: true });
export const isMaintenance = routes => _.some(routes, { maintenance: true });
export const isGeoLocationBlocked = routes => _.some(routes, { geolocationBlocked: true });

/**
 * Checks if a component exists in the list of components that should be excluded when
 * appView is enabled
 * @param {String} - Component name
 * @return {Boolean} - True if component name exists in APPVIEW_EXCLUDE array.  False otherwise.
 */
export const isAppViewExcluded = componentName => _.includes(APPVIEW_EXCLUDE, componentName);

export const getLocation = href => {
  const loc = document.createElement('a');
  loc.href = href;
  const { hostname, pathname } = loc;
  return {
    hostname,
    pathname,
  };
};

export const refreshPage = () => {
  if (window && window.location && window.location.href) {
    window.location.href = window.location.href.replace(window.location.hash, '');
  }
};

export const redirectToExternalUrl = externalUrl => {
  if (window && window.location && window.location.href) {
    window.location.href = externalUrl;
  }
};

export const getCampaignOverrides = () => {
  const search = _.get(window, 'location.search');
  if (!search) return {};
  const searchParts = _.split(_.trimStart(search, '?'), '::');
  const overrides = {};
  const urlParamTypes = [
    { from: 'cmpid=org=', to: 'afSub2' },
    { from: 'ag=', to: 'afPrt' },
    { from: 'mc=', to: 'referral' },
    { from: 'src=', to: 'afChannel' },
    { from: 'cmp=', to: 'c' },
    { from: 'add=', to: 'afSub1' },
  ];
  _.forEach(searchParts, searchPart => {
    const typeIndex = _.findIndex(urlParamTypes, urlParamType =>
      _.startsWith(searchPart, urlParamType.from),
    );
    if (typeIndex >= 0) {
      const urlParamType = urlParamTypes[typeIndex];
      const paramValue = searchPart.substr(urlParamType.from.length);
      overrides[urlParamType.to] = paramValue;
    }
  });
  return overrides;
};

export function onRouteChange(prevState, nextState, replace) {
  if (!window.player) window.player = {};
  const prevPath = _.get(prevState, 'location.pathname');
  helperForceTrailingSlash(replace);
  setOptOutCCPACookie();
  setSessionStorageItem({ key: LAST_SESSION_PATH, value: prevPath });
}

export function getPreviousPathname() {
  return getSessionStorageItem({ key: LAST_SESSION_PATH });
}

export function setSettingsPath(path) {
  setSessionStorageItem({ key: LAST_SETTING_PATH, value: path });
}

export function getPreviousSettingPath() {
  return getSessionStorageItem({ key: LAST_SETTING_PATH });
}

export const getDomain = () => {
  const urlItems = BaseUrl && BaseUrl.split('.');
  if (_.isEmpty(urlItems)) return null;
  urlItems.shift();
  const domain = urlItems.join('.');
  return domain;
};

const urlAppParser = (locationObj, appUrl) => {
  if (!appUrl) return null;
  const url = locationObj.pathname;
  // Video Url
  if (_.includes(url, '/watch/')) {
    const watchUrl = url.slice(1);
    return `${appUrl}${watchUrl}`;
  }
  // Movie Url
  if (_.includes(url, '/movies/')) {
    const movieUrl = url.replace('/movies', 'movieDetail');
    return `${appUrl}${movieUrl}`;
  }
  // Series Detail Url - any character within / /
  if (url.match(/^\/([^/]*)\/$/)) {
    return `${appUrl}seriesDetail${url}`;
  }
  return appUrl;
};

export const redirectSalesForceLinksiOS = () => {
  const brand = _.get(window.ExternalConfig, 'brand.businessUnit');
  const appLinks = {
    fox: 'foxapp://',
  };
  const appUrl = appLinks[brand];
  if (appUrl === undefined || !SalesforceMobileLinksRedirect) return;
  // iOS mobile devices only
  const UA = navigator.userAgent;
  const appUrlList = getAppUrlList({});
  const originalLink = window.location.href;
  const currentLocation = window.location;
  if (UA.match(/iPhone|iPod/i) != null || (UA.match(/iPad/) && _.get(appUrlList, 'ios'))) {
    // Only redirect email campaigns, prevent loop with hash
    // Redirect if the appLink fails
    if (
      _.includes(window.location.search, 'mc=email') &&
      !_.includes(window.location.search, '#noApp')
    ) {
      const deepLink = urlAppParser(currentLocation, appUrl);
      setTimeout(() => {
        window.location = `${originalLink}#noApp`;
      }, 200);
      window.location = deepLink;
    }
  }
};

const getExternalSignInUrl = () => _.get(window.ExternalConfig, 'externalSignInUrl');

export const goToExternalSignIn = () => {
  const signInUrl = getExternalSignInUrl();
  window.location = signInUrl;
};

export const getAuthRedirectUrl = () => {
  const baseUrlSlug = helperGetBaseUrlSlug();
  return `${BaseUrl}${baseUrlSlug}/static/html/auth_success_landing.html`;
};

export const getUrlParamsFromHash = hash => {
  const hashString = _.replace(hash, '#', '');
  const hashSegments = hashString.split('&');
  const params = {};
  _.forEach(hashSegments, param => {
    const items = param.split('=');
    const key = items[0];
    const value = items[1];
    params[key] = value;
  });
  return params;
};

export const getUrlParams = url => {
  const params = {};
  if (!url || !_.includes(url, '?')) return params;
  const paramString = url.split('?').pop();
  const paramPairs = paramString.split('&');
  _.forEach(paramPairs, pair => {
    const pairArray = pair.split('=');
    const key = pairArray[0];
    const value = pairArray[1];
    if (key && value) params[key] = value;
  });
  return params;
};

export const removeUnsafeUrlParams = () => {
  const searchString = window.location.search;
  if (searchString) {
    const params = getUrlParams(searchString);
    const removeUnsafeEmailParam = _.get(params, 'email');
    if (removeUnsafeEmailParam) {
      _.unset(params, 'email');
      const newSearchParams = _.map(params, (value, key) => `${key}=${value}`);
      const newSearchString = _.isEmpty(newSearchParams) ? '' : `?${newSearchParams.join('&')}`;
      if (newSearchString !== searchString) {
        const newUrl = window.location.href.replace(searchString, newSearchString);
        window.location.replace(newUrl);
      }
    }
  }
};
const getCurrentPath = () => _.get(window, 'location.pathname');

export const isUrlPath = path => {
  const currentPath = getCurrentPath();
  return currentPath === path;
};

export const isHomeRoute = str => {
  const featuredPath = /\/featured((\/\w+)+|\/?)$/;
  const homePath = /^\/$/;
  return [featuredPath, homePath].some(regex => regex.test(str));
};

export const setPathForReEntryRedirect = (path = null) => {
  if (path === '/') return;
  const currentPath = getCurrentPath();
  const value = path || currentPath;
  setCookie({ name: COOKIE_SET_PATH_FOR_RE_ENTRY_REDIRECT, value, options: { path: '/' } });
};

export const isFoxSoulResetPasswordPage = window.location.pathname.includes(
  'reset-password/fox-soul',
);

export const isResetPage = window.location.pathname.includes('reset-password/');
